/**
 * Document sync service - Yjs integration with Protocol v1 encryption & wire contract
 */

import * as Y from 'yjs';
import { OPFSPersistence, clearAllOPFSDocuments } from './OPFSPersistence.js';
import {
  getCurrentEpoch,
  deriveEpochKey,
  getOrCreateDeviceKey,
  openEpochEnvelope,
  sealEpochEnvelope,
  type EnvelopeHeaderKind,
} from '@yada/crypto';

import type {
  ManifestDoc,
  DocumentRead,
} from '@yada/storage-protocol';
import { getWorkspaceSeed, clearSeedCache } from './workspaceSeedManager.js';
import { clearLocalWrappedSeedVault } from './localSeedStorage.js';
import { LocalPurgeError, clearAppDatabases, deleteIndexedDatabase } from './localPurge.js';
import { DOCUMENT_METADATA_DB } from './documentMetadata.js';
import { ASSET_CACHE_DB, clearAssetBlobCache } from './assetManager.js';
import { WORKSPACE_HYDRATION_JOURNAL_DB } from './workspaceHydrationJournal.js';
import * as storageClient from '@yada/api-client';
import { withRetry } from './retry.js';
import { logger } from '@yada/logger';
import {
  recordLocalDocSync,
  getAllLocalDocSync,
  clearAllLocalDocSync,
  getNextWriterCounter,
} from './localSyncState.js';
import { isSignalingConnected, sendCollabUpdate, onCollabUpdate } from './signalingClient.js';
import { getCurrentPresence, notifyDocumentUpdated } from './syncPushClient.js';

let _syncConflictHandler: ((message: string) => void) | null = null;
export function setSyncConflictHandler(handler: (message: string) => void): void {
  _syncConflictHandler = handler;
}

export interface SyncedDocument {
  doc: Y.Doc;
  remoteDoc?: Y.Doc;
  workspaceId: string;
  documentId: string;
  docType: string;
  persistence: OPFSPersistence;
  syncing: boolean;
  version: number;
  head?: string;
  covers?: Record<string, number>;
}

export const activeDocuments = new Map<string, SyncedDocument>();

// ── Global sync state observable ─────────────────────────────────────────────

export interface GlobalSyncState {
  hasPending: boolean;
  hasSyncing: boolean;
  error: { reason: 'offline' | 'auth' | 'seed-unavailable' | 'unknown'; since: Date } | null;
}

let globalSyncState: GlobalSyncState = { hasPending: false, hasSyncing: false, error: null };
const syncStateListeners = new Set<(state: GlobalSyncState) => void>();
const syncErrors = new Map<string, NonNullable<GlobalSyncState['error']>>();

function recordSyncResult(scope: string, error?: unknown): void {
  if (error !== undefined) syncErrors.set(scope, { reason: classifySyncError(error), since: new Date() });
  else syncErrors.delete(scope);
  updateSyncState({ error: syncErrors.values().next().value ?? null });
}

const documentOperations = new Map<string, Promise<void>>();

async function runDocumentOperation(synced: SyncedDocument, operation: () => Promise<void>): Promise<void> {
  const previous = documentOperations.get(synced.documentId) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(async () => {
    synced.syncing = true;
    updateSyncState({ hasSyncing: true });
    try {
      await operation();
      recordSyncResult(synced.documentId);
    } catch (err) {
      recordSyncResult(synced.documentId, err);
      if (err instanceof storageClient.StorageError && err.status === 409) {
        _syncConflictHandler?.('Sync conflict could not be resolved. Your changes remain pending on this device.');
      }
      throw err;
    } finally {
      synced.syncing = false;
    }
  });
  documentOperations.set(synced.documentId, next);
  try {
    await next;
  } finally {
    if (documentOperations.get(synced.documentId) === next) documentOperations.delete(synced.documentId);
    updateSyncState({ hasSyncing: documentOperations.size > 0 });
  }
}

function updateSyncState(patch: Partial<GlobalSyncState>): void {
  globalSyncState = { ...globalSyncState, ...patch };
  syncStateListeners.forEach((cb) => cb(globalSyncState));
}

export function getGlobalSyncState(): GlobalSyncState {
  return globalSyncState;
}

export function onSyncStateChange(cb: (state: GlobalSyncState) => void): () => void {
  syncStateListeners.add(cb);
  return () => syncStateListeners.delete(cb);
}

// ── Helpers for Base64 / Envelope ────────────────────────────────────────────

function uint8ArrayToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function stateFingerprint(doc: Y.Doc): Promise<string> {
  const bytes = Y.encodeStateAsUpdate(doc);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(bytes)));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function hasUnsentYjsChanges(localDoc: Y.Doc, remoteDoc: Y.Doc): boolean {
  const localSV = Y.decodeStateVector(Y.encodeStateVector(localDoc));
  const remoteSV = Y.decodeStateVector(Y.encodeStateVector(remoteDoc));
  for (const [client, clock] of localSV.entries()) {
    if (clock > (remoteSV.get(client) ?? 0)) {
      return true;
    }
  }
  const localClients = (localDoc.store as { clients?: Map<number, Array<{ id: { clock: number }; length: number; deleted: boolean }>> }).clients;
  const remoteClients = (remoteDoc.store as { clients?: Map<number, Array<{ id: { clock: number }; length: number; deleted: boolean }>> }).clients;
  if (!localClients || !remoteClients) {
    const localState = Y.encodeStateAsUpdate(localDoc);
    const remoteState = Y.encodeStateAsUpdate(remoteDoc);
    return localState.length !== remoteState.length || localState.some((byte, i) => byte !== remoteState[i]);
  }
  for (const [client, structs] of localClients.entries()) {
    const rStructs = remoteClients.get(client);
    if (!rStructs) {
      if (structs.some((s) => s.deleted)) return true;
      continue;
    }
    let rIdx = 0;
    for (const s of structs) {
      if (!s.deleted) continue;
      const endClock = s.id.clock + s.length;
      let clock = s.id.clock;
      while (clock < endClock) {
        while (rIdx < rStructs.length && rStructs[rIdx].id.clock + rStructs[rIdx].length <= clock) {
          rIdx++;
        }
        if (rIdx >= rStructs.length) return true;
        const rs = rStructs[rIdx];
        if (rs.id.clock > clock || !rs.deleted) return true;
        clock = Math.min(endClock, rs.id.clock + rs.length);
      }
    }
  }
  return false;
}

function base64ToUint8Array(base64: string): Uint8Array {
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(base64, 'base64'));
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function decryptBlobPayload(
  blobBase64: string,
  workspaceId: string,
  documentId: string,
  kind: EnvelopeHeaderKind,
  seed: ArrayBuffer
): Promise<Uint8Array> {
  const { plaintext } = await openEpochEnvelope(
    base64ToUint8Array(blobBase64),
    { workspaceId, kind, objectId: documentId },
    async (epoch) => (await deriveEpochKey(workspaceId, seed, epoch)).key
  );
  return plaintext;
}

// ── Ephemeral co-editing relay listener ──────────────────────────────────────
onCollabUpdate(async (workspaceId, documentId, blob, fromDeviceId) => {
  const synced = activeDocuments.get(documentId);
  if (!synced || synced.workspaceId !== workspaceId) return;
  try {
    const myDeviceKey = await getOrCreateDeviceKey().catch(() => null);
    if (myDeviceKey && fromDeviceId && myDeviceKey.deviceId === fromDeviceId) {
      return; // Skip own updates
    }
    const seed = await getWorkspaceSeed(workspaceId);
    const decrypted = await decryptBlobPayload(blob, workspaceId, documentId, 'update', seed);
    Y.applyUpdate(synced.doc, decrypted, 'sync:collab');
  } catch (err) {
    logger.debug('documentSync', `Failed to apply ephemeral collab update for ${documentId}:`, err);
  }
});

async function createEncryptedUpdateEnvelope(
  workspaceId: string,
  documentId: string,
  update: Uint8Array,
  seed: ArrayBuffer
): Promise<string> {
  const epoch = getCurrentEpoch().epoch;
  const envelope = await sealEpochEnvelope(update, { workspaceId, kind: 'update', objectId: documentId }, {
    epoch,
    key: (await deriveEpochKey(workspaceId, seed, epoch)).key,
    header: { content: 'yjs-update-v1' },
  });
  return uint8ArrayToBase64(envelope);
}

async function createEncryptedSnapshotEnvelope(
  workspaceId: string,
  documentId: string,
  snapshot: Uint8Array,
  seed: ArrayBuffer,
  docType?: string
): Promise<string> {
  const epoch = getCurrentEpoch().epoch;
  const envelope = await sealEpochEnvelope(snapshot, { workspaceId, kind: 'snapshot', objectId: documentId }, {
    epoch,
    key: (await deriveEpochKey(workspaceId, seed, epoch)).key,
    header: { doc_type: docType, content: 'yjs-snapshot-v1' },
  });
  return uint8ArrayToBase64(envelope);
}

const pendingOpens = new Map<string, Promise<SyncedDocument>>();
const pendingOpenSyncs = new Map<string, { workspaceId: string; documentId: string; docType: string }>();
let openSyncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const latestManifestByWorkspace = new Map<string, Map<string, ManifestDoc>>();

function queueDocumentRemoteSync(workspaceId: string, documentId: string, docType: string): void {
  pendingOpenSyncs.set(documentId, { workspaceId, documentId, docType });
  if (openSyncDebounceTimer !== null) return;

  openSyncDebounceTimer = setTimeout(async () => {
    openSyncDebounceTimer = null;
    const items = Array.from(pendingOpenSyncs.values());
    pendingOpenSyncs.clear();
    if (items.length === 0) return;

    if (items.length === 1) {
      const item = items[0];
      const synced = activeDocuments.get(item.documentId);
      if (synced) {
        try {
          await getWorkspaceSeed(item.workspaceId);
        } catch {
          // If seed is unavailable (e.g. pending device waiting for pairing), proceed to sync attempt/lock
        }
        const cachedManifest = latestManifestByWorkspace.get(item.workspaceId);
        if (cachedManifest) {
          const mDoc = cachedManifest.get(item.documentId);
          if (!mDoc) {
            if (Y.encodeStateAsUpdate(synced.doc).length > 2) {
              syncFromRemote(synced).catch(() => {});
            }
            return;
          }
          if (synced.head && synced.head === mDoc.head && synced.remoteDoc && !hasUnsentYjsChanges(synced.doc, synced.remoteDoc)) {
            return;
          }
        }
        syncFromRemote(synced).catch((err) => {
          logger.debug('openDocument', `Background sync for ${item.documentId} completed with fallback:`, err);
        });
      }
      return;
    }

    const byWorkspace = new Map<string, string[]>();
    for (const item of items) {
      const list = byWorkspace.get(item.workspaceId) || [];
      list.push(item.documentId);
      byWorkspace.set(item.workspaceId, list);
    }

    for (const [wsId, docIds] of byWorkspace.entries()) {
      try {
        try {
          await getWorkspaceSeed(wsId);
        } catch {
          // Non-blocking
        }
        const localRecords = await getAllLocalDocSync();
        const cachedManifest = latestManifestByWorkspace.get(wsId);
        const docsToFetch = docIds.filter((id) => {
          const synced = activeDocuments.get(id);
          if (!synced) return false;
          if (cachedManifest) {
            const mDoc = cachedManifest.get(id);
            if (!mDoc) {
              // Document does not exist on server; only push if it has local content
              if (Y.encodeStateAsUpdate(synced.doc).length > 2) {
                syncFromRemote(synced).catch(() => {});
              }
              return false;
            }
            if (synced.head && synced.head === mDoc.head && synced.remoteDoc && !hasUnsentYjsChanges(synced.doc, synced.remoteDoc)) {
              return false;
            }
          }
          return true;
        });
        if (docsToFetch.length === 0) continue;
        const batchResults = await storageClient.getDocumentsBatch(wsId, docsToFetch.map((id) => {
          const synced = activeDocuments.get(id);
          const record = localRecords.get(id);
          const since = synced?.remoteDoc ? synced.head : (record?.head && record.stateFingerprint ? record.head : undefined);
          return since ? { id, since } : { id };
        }));
        const resultMap = new Map(batchResults.map((r) => [r.id, r]));
        for (const docId of docsToFetch) {
          const synced = activeDocuments.get(docId);
          if (synced) {
            const batchResult = resultMap.get(docId);
            syncFromRemote(synced, batchResult).catch((err) => {
              logger.debug('openDocument', `Batch background sync for ${docId} completed with fallback:`, err);
            });
          }
        }
      } catch (err) {
        logger.warn('openDocument', `Batch background sync failed for workspace ${wsId}, falling back to individual sync:`, err);
        for (const docId of docIds) {
          const synced = activeDocuments.get(docId);
          if (synced) {
            syncFromRemote(synced).catch(() => {});
          }
        }
      }
    }
  }, 50);
}

export async function openDocument(
  workspaceId: string,
  documentId: string,
  docType: string,
  options: { initialRemoteSync?: boolean } = {}
): Promise<SyncedDocument> {
  const existing = activeDocuments.get(documentId);
  if (existing) return existing;

  const pending = pendingOpens.get(documentId);
  if (pending) return pending;

  const openPromise = (async () => {
    const doc = new Y.Doc();
    const persistence = new OPFSPersistence(documentId, doc);

    await new Promise<void>((resolve) => {
      persistence.on('synced', () => resolve());
    });

    const account = await import('@yada/storage').then((s) => s.getAccount());
    const isAccountless = account?.isAccountless;
    const isUnconfirmed = account?.emailVerified === false;
    const isRemoteSyncDisabled = isAccountless || isUnconfirmed;

    const synced: SyncedDocument = {
      doc,
      workspaceId,
      documentId,
      docType,
      persistence,
      syncing: false,
      version: 0,
    };

    try {
      const localRecord = (await getAllLocalDocSync()).get(documentId);
      if (localRecord?.head && localRecord.stateFingerprint) {
        if ((await stateFingerprint(doc)) === localRecord.stateFingerprint) {
          synced.head = localRecord.head;
          synced.covers = localRecord.covers ? { ...localRecord.covers } : undefined;
          synced.remoteDoc = new Y.Doc();
          Y.applyUpdate(synced.remoteDoc, Y.encodeStateAsUpdate(doc), 'sync:remote');
        }
      }
    } catch {
      // Non-fatal
    }

    activeDocuments.set(documentId, synced);

    const shouldSync = options.initialRemoteSync !== false && !isRemoteSyncDisabled;
    if (shouldSync) {
      queueDocumentRemoteSync(workspaceId, documentId, docType);
    }

    doc.on('update', (update: Uint8Array, origin: unknown) => {
      if (origin === 'sync:remote' || origin === 'sync:collab') return;
      // Queue synchronously so an immediate flush cannot miss this edit.
      // Account eligibility is checked at upload time, since it can change.
      void syncUpdateToRemote(synced, update);
    });

    return synced;
  })();

  pendingOpens.set(documentId, openPromise);

  try {
    return await openPromise;
  } finally {
    pendingOpens.delete(documentId);
  }
}

export async function resolveRemoteDocumentId(
  preferredDocumentId: string,
  legacyDocumentId: string | undefined,
  workspaceId: string = 'default'
): Promise<string> {
  if (!legacyDocumentId || legacyDocumentId === preferredDocumentId) return preferredDocumentId;

  // 1. Check local OPFS files and sync records first — local data is always primary and offline-available
  try {
    const { listAllOPFSDocumentIds } = await import('./OPFSPersistence.js');
    const localIds = new Set(await listAllOPFSDocumentIds());
    if (localIds.has(preferredDocumentId)) return preferredDocumentId;
    if (localIds.has(legacyDocumentId)) return legacyDocumentId;
    const syncRecords = await getAllLocalDocSync();
    if (syncRecords.has(preferredDocumentId)) return preferredDocumentId;
    if (syncRecords.has(legacyDocumentId)) return legacyDocumentId;
  } catch {
    // Non-blocking fallback
  }

  // 2. Check cached manifest if available before issuing individual HEAD requests
  const cachedManifest = latestManifestByWorkspace.get(workspaceId);
  if (cachedManifest) {
    if (cachedManifest.has(preferredDocumentId)) return preferredDocumentId;
    if (cachedManifest.has(legacyDocumentId)) return legacyDocumentId;
    return preferredDocumentId;
  }

  // 3. Fall back to remote discovery if not found locally
  try {
    const hasPreferred = await storageClient.hasDocument(workspaceId, preferredDocumentId);
    if (hasPreferred) return preferredDocumentId;
  } catch {
    // Non-blocking fallback
  }

  try {
    const hasLegacy = await storageClient.hasDocument(workspaceId, legacyDocumentId);
    if (hasLegacy) {
      logger.log('documentSync', `Using legacy document ID for existing data: ${legacyDocumentId}`);
      return legacyDocumentId;
    }
  } catch {
    // Non-blocking fallback
  }
  return preferredDocumentId;
}

export async function createDocument(
  workspaceId: string,
  docType: string,
  documentId?: string
): Promise<SyncedDocument> {
  const finalDocumentId = documentId || crypto.randomUUID();
  const doc = new Y.Doc();
  const persistence = new OPFSPersistence(finalDocumentId, doc);

  await new Promise<void>((resolve) => {
    persistence.on('synced', () => resolve());
  });

  const account = await import('@yada/storage').then((s) => s.getAccount());
  const isRemoteSyncDisabled = account?.isAccountless || account?.emailVerified === false;

  const synced: SyncedDocument = {
    doc,
    workspaceId,
    documentId: finalDocumentId,
    docType,
    persistence,
    syncing: false,
    version: 0,
  };

  activeDocuments.set(finalDocumentId, synced);

  if (!isRemoteSyncDisabled) {
    await syncToRemote(synced);
  }

  doc.on('update', (update: Uint8Array, origin: unknown) => {
    if (origin === 'sync:remote' || origin === 'sync:collab') return;
    void syncUpdateToRemote(synced, update);
  });

  return synced;
}

export async function syncToRemote(synced: SyncedDocument): Promise<void> {
  const account = await import('@yada/storage').then((s) => s.getAccount());
  if (account?.isAccountless || account?.emailVerified === false) return;
  await runDocumentOperation(synced, () => writeSnapshot(synced));
}

// Only acknowledge a head after every encrypted payload has been decoded and
// applied successfully. Staging also prevents partially applying a corrupt read.
async function applyRemoteDocument(synced: SyncedDocument, result: DocumentRead, seed: ArrayBuffer): Promise<Y.Doc> {
  const remote = await decodeRemoteDocument(result, synced.workspaceId, synced.documentId, seed);
  try {
    Y.applyUpdate(synced.doc, Y.encodeStateAsUpdate(remote), 'sync:remote');
    if (!synced.remoteDoc) synced.remoteDoc = new Y.Doc();
    Y.applyUpdate(synced.remoteDoc, Y.encodeStateAsUpdate(remote), 'sync:remote');
    const covers: Record<string, number> = {
      ...(synced.covers ?? {}),
      ...(result.snapshot?.covers ?? {}),
    };
    for (const update of result.updates) {
      covers[update.writer] = Math.max(covers[update.writer] ?? 0, update.counter);
    }
    synced.covers = covers;
    synced.head = result.head;
    const wsManifest = latestManifestByWorkspace.get(synced.workspaceId);
    if (wsManifest) {
      wsManifest.set(synced.documentId, { id: synced.documentId, head: result.head, tombstone: false });
    }
    const converged = !hasUnsentYjsChanges(synced.doc, synced.remoteDoc);
    const fingerprint = converged ? await stateFingerprint(synced.doc) : await stateFingerprint(synced.remoteDoc);
    await recordLocalDocSync(synced.documentId, synced.workspaceId, result.head, undefined, undefined, fingerprint, covers);
    return remote;
  } catch (err) {
    remote.destroy();
    throw err;
  }
}

// The document id comes from the request, never from the node's response, so a node
// cannot pass another document's ciphertext off as this one.
async function decodeRemoteDocument(result: DocumentRead, workspaceId: string, documentId: string, seed: ArrayBuffer): Promise<Y.Doc> {
  const remote = new Y.Doc();
  try {
    // Legacy documents migrated without a snapshot carry an empty snapshot blob: no content, not plaintext.
    if (result.snapshot?.blob) {
      Y.applyUpdate(remote, await decryptBlobPayload(result.snapshot.blob, workspaceId, documentId, 'snapshot', seed));
    }
    for (const update of result.updates) {
      Y.applyUpdate(remote, await decryptBlobPayload(update.blob, workspaceId, documentId, 'update', seed));
    }
    return remote;
  } catch (err) {
    remote.destroy();
    throw err;
  }
}

async function verifyLocalDocumentOnServer(synced: SyncedDocument): Promise<void> {
  const seed = await getWorkspaceSeed(synced.workspaceId);
  const stored = await storageClient.getDocument(synced.workspaceId, synced.documentId);
  const remote = await decodeRemoteDocument(stored, synced.workspaceId, synced.documentId, seed);
  try {
    if (hasUnsentYjsChanges(synced.doc, remote)) {
      throw new Error(`Storage accepted a write but is missing local content for ${synced.documentId}`);
    }
    const covers = { ...stored.snapshot?.covers };
    for (const update of stored.updates) {
      covers[update.writer] = Math.max(covers[update.writer] ?? 0, update.counter);
    }
    synced.covers = covers;
    synced.head = stored.head;
    if (!synced.remoteDoc) synced.remoteDoc = new Y.Doc();
    Y.applyUpdate(synced.remoteDoc, Y.encodeStateAsUpdate(remote), 'sync:remote');
    await recordLocalDocSync(synced.documentId, synced.workspaceId, stored.head, undefined, undefined, await stateFingerprint(synced.remoteDoc), covers);
  } finally {
    remote.destroy();
  }
}

async function writeSnapshot(synced: SyncedDocument): Promise<void> {
  const seed = await getWorkspaceSeed(synced.workspaceId);
  const { deviceId: writer } = await getOrCreateDeviceKey();
  await withRetry(async () => {
    // Merge before replacing a snapshot, including all writers' coverage.
    // A 409 retries this read directly, without re-entering the document queue.
    try {
      const result = await storageClient.getDocument(synced.workspaceId, synced.documentId);
      const remote = await applyRemoteDocument(synced, result, seed);
      remote.destroy();
    } catch (err) {
      if (!(err instanceof storageClient.StorageError) || err.status !== 404) throw err;
    }
    const counter = await getNextWriterCounter(synced.documentId, synced.covers?.[writer] ?? 0);
    const covers = { ...synced.covers, [writer]: counter };
    const snapshotState = Y.encodeStateAsUpdate(synced.doc);
    const blob = await createEncryptedSnapshotEnvelope(
      synced.workspaceId, synced.documentId, snapshotState, seed, synced.docType
    );
    const snapshotRes: unknown = await storageClient.putSnapshot(synced.workspaceId, synced.documentId, {
      id: `s_${crypto.randomUUID()}`, covers, blob,
    });
    synced.covers = covers;
    if (!synced.remoteDoc) synced.remoteDoc = new Y.Doc();
    Y.applyUpdate(synced.remoteDoc, snapshotState, 'sync:remote');
    if (snapshotRes && typeof snapshotRes === 'object' && 'cursor' in snapshotRes && typeof (snapshotRes as any).cursor === 'string') {
      const cursor = (snapshotRes as any).cursor as string;
      synced.head = cursor;
      const wsManifest = latestManifestByWorkspace.get(synced.workspaceId);
      if (wsManifest) {
        wsManifest.set(synced.documentId, { id: synced.documentId, head: cursor, tombstone: false });
      }
      const converged = !hasUnsentYjsChanges(synced.doc, synced.remoteDoc);
      const fingerprint = converged ? await stateFingerprint(synced.doc) : await stateFingerprint(synced.remoteDoc);
      await recordLocalDocSync(synced.documentId, synced.workspaceId, cursor, undefined, undefined, fingerprint, covers);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('yada:lastSyncTimestamp', Date.now().toString());
    }
  }, {
    maxRetries: 3,
    shouldRetry: (err) => classifySyncError(err) === 'offline' ||
      (err instanceof storageClient.StorageError && err.status === 409),
  });
}

export async function retrySyncForAllDocuments(): Promise<void> {
  const results = await Promise.allSettled(
    Array.from(activeDocuments.values(), (synced) => syncFromRemote(synced))
  );
  const failed = results.find((r) => r.status === 'rejected');
  if (failed?.status === 'rejected') throw failed.reason;
}

export async function computeManifestDiff(
  manifest: ManifestDoc[]
): Promise<ManifestDoc[]> {
  const { listAllOPFSDocumentIds } = await import('./OPFSPersistence.js');
  const opfsDocIds = new Set(await listAllOPFSDocumentIds());
  const localSyncState = await getAllLocalDocSync();

  const diff: ManifestDoc[] = [];

  for (const item of manifest) {
    if (item.tombstone) continue;

    const active = activeDocuments.get(item.id);
    if (active) {
      const isStale = active.head !== item.head;
      if (isStale) {
        diff.push(item);
      }
      continue;
    }

    if (!opfsDocIds.has(item.id)) {
      diff.push(item);
      continue;
    }

    const record = localSyncState.get(item.id);
    if (!record || record.head !== item.head) {
      diff.push(item);
      continue;
    }
  }

  return diff;
}

export async function batchPullManifestDiff(
  workspaceId: string,
  diffItems: ManifestDoc[]
): Promise<void> {
  if (diffItems.length === 0) return;
  const localSyncState = await getAllLocalDocSync();
  const queries = diffItems.map((it) => {
    const active = activeDocuments.get(it.id);
    const record = localSyncState.get(it.id);
    const since = active?.remoteDoc ? active.head : record?.stateFingerprint ? record.head : undefined;
    return since ? { id: it.id, since } : { id: it.id };
  });
  const remoteDocs = await storageClient.getDocumentsBatch(workspaceId, queries);
  const remoteDocMap = new Map(remoteDocs.map((r) => [r.id, r]));
  const results = await Promise.allSettled(diffItems.map(async (item) => {
    const remoteData = remoteDocMap.get(item.id);
    if (!remoteData) throw new Error(`Batch response omitted document ${item.id}`);
    const synced = activeDocuments.get(item.id) ??
      await openDocument(workspaceId, item.id, 'doc', { initialRemoteSync: false });
    await syncFromRemote(synced, remoteData);
    // syncFromRemote records the actual read head, which can be newer than the manifest.
    notifyDocumentUpdated(item.id);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('yada:document:updated', { detail: { documentId: item.id } }));
    }
  }));
  const failed = results.find((r) => r.status === 'rejected');
  if (failed?.status === 'rejected') throw failed.reason;
}

export async function syncDocumentManifest(workspaceId: string = 'default'): Promise<{
  totalManifestCount: number;
  diffCount: number;
  pulledCount: number;
}> {
  const account = await import('@yada/storage').then((s) => s.getAccount());
  if (account?.isAccountless || account?.emailVerified === false) {
    return { totalManifestCount: 0, diffCount: 0, pulledCount: 0 };
  }
  const scope = `manifest:${workspaceId}`;
  try {
    const { documents: manifest } = await storageClient.getDocumentManifest(workspaceId);
    latestManifestByWorkspace.set(
      workspaceId,
      new Map(manifest.filter((d) => !d.tombstone).map((d) => [d.id, d]))
    );
    const diff = await computeManifestDiff(manifest);
    await batchPullManifestDiff(workspaceId, diff);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('yada:lastSyncTimestamp', Date.now().toString());
    }
    recordSyncResult(scope);
    return { totalManifestCount: manifest.length, diffCount: diff.length, pulledCount: diff.length };
  } catch (err) {
    recordSyncResult(scope, err);
    throw err;
  }
}

export async function syncAllWorkspaceDocuments(workspaceId: string): Promise<void> {
  await syncDocumentManifest(workspaceId);
}

export async function syncFromRemote(synced: SyncedDocument, prefetchedResult?: DocumentRead): Promise<void> {
  const account = await import('@yada/storage').then((s) => s.getAccount());
  if (account?.isAccountless || account?.emailVerified === false) return;
  await runDocumentOperation(synced, async () => {
    const seed = await getWorkspaceSeed(synced.workspaceId);
    let result: DocumentRead;
    try {
      result = prefetchedResult ?? await withRetry(
        () => storageClient.getDocument(synced.workspaceId, synced.documentId, synced.head),
        { maxRetries: 3, shouldRetry: (err) => classifySyncError(err) === 'offline' }
      );
    } catch (err) {
      if (!(err instanceof storageClient.StorageError) || err.status !== 404) throw err;
      if (Y.encodeStateAsUpdate(synced.doc).length > 2) await writeSnapshot(synced);
      return;
    }
    // A partial response has no baseline on a freshly opened document. Trust
    // the OPFS baseline only when it matches the state recorded at server ack.
    // Otherwise read the full remote state so unsent local edits can be recovered.
    if (!result.snapshot && !synced.remoteDoc) {
      const record = (await getAllLocalDocSync()).get(synced.documentId);
      if (record?.stateFingerprint && record.head) {
        if (await stateFingerprint(synced.doc) === record.stateFingerprint) {
          synced.remoteDoc = new Y.Doc();
          Y.applyUpdate(synced.remoteDoc, Y.encodeStateAsUpdate(synced.doc), 'sync:remote');
        } else {
          result = await storageClient.getDocument(synced.workspaceId, synced.documentId);
        }
      }
    }
    const remote = await applyRemoteDocument(synced, result, seed);
    // If incoming update list is large (>= MAX_UPDATES_BEFORE_COMPACT), schedule compaction
    if (result.updates && result.updates.length >= MAX_UPDATES_BEFORE_COMPACT) {
      void writeSnapshot(synced).catch(e => logger.warn('documentSync', 'Compaction after large pull failed:', e));
    }
    try {
      // Recover local edits missed by earlier failed uploads or a browser restart.
      const comparisonTarget = synced.remoteDoc ?? remote;
      if (hasUnsentYjsChanges(synced.doc, comparisonTarget)) {
        const missingDiff = Y.encodeStateAsUpdate(synced.doc, Y.encodeStateVector(comparisonTarget));
        if (missingDiff.byteLength > 2) {
          await syncUpdateToRemote(synced, missingDiff);
        }
      }
    } finally {
      remote.destroy();
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('yada:lastSyncTimestamp', Date.now().toString());
    }
  });
}

const pendingUpdateBuffers = new Map<string, Uint8Array[]>();
const updateCountSinceCompact = new Map<string, number>();
const updateBytesSinceCompact = new Map<string, number>();
const MAX_UPDATES_BEFORE_COMPACT = 50;
const MAX_BYTES_BEFORE_COMPACT = 40 * 1024; // 40KB accumulated update payload
const IDLE_COMPACT_DELAY_MS = 30_000;
const MIN_UPDATES_FOR_IDLE_COMPACT = 25;
const pendingSyncTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const pendingIdleCompacts = new Map<string, ReturnType<typeof setTimeout>>();

export const SYNC_DEBOUNCE_COEDITING_MS = 2_000;
export const SYNC_DEBOUNCE_SOLO_MS = 10_000;
export const SYNC_DEBOUNCE_TEST_MS = 400;

export function getAdaptiveDebounceMs(): number {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return SYNC_DEBOUNCE_TEST_MS;
  }
  const presence = getCurrentPresence();
  return presence.length > 1 ? SYNC_DEBOUNCE_COEDITING_MS : SYNC_DEBOUNCE_SOLO_MS;
}

function scheduleIdleCompaction(synced: SyncedDocument): void {
  const { documentId } = synced;
  const existing = pendingIdleCompacts.get(documentId);
  if (existing) clearTimeout(existing);
  const count = updateCountSinceCompact.get(documentId) ?? 0;
  const bytes = updateBytesSinceCompact.get(documentId) ?? 0;
  if (count < MIN_UPDATES_FOR_IDLE_COMPACT && bytes < MAX_BYTES_BEFORE_COMPACT) {
    return;
  }
  pendingIdleCompacts.set(documentId, setTimeout(() => {
    pendingIdleCompacts.delete(documentId);
    const currentCount = updateCountSinceCompact.get(documentId) ?? 0;
    const currentBytes = updateBytesSinceCompact.get(documentId) ?? 0;
    if ((currentCount >= MIN_UPDATES_FOR_IDLE_COMPACT || currentBytes >= MAX_BYTES_BEFORE_COMPACT) && !synced.syncing && (pendingUpdateBuffers.get(documentId)?.length ?? 0) === 0) {
      compactDocument(synced).catch((err) =>
        logger.warn('documentSync', `Idle compaction failed for ${documentId}:`, err)
      );
    }
  }, IDLE_COMPACT_DELAY_MS));
}

async function flushDocumentUpdates(synced: SyncedDocument): Promise<void> {
  await runDocumentOperation(synced, async () => {
    const { documentId } = synced;
    const updates = pendingUpdateBuffers.get(documentId);
    if (!updates?.length) return;
    pendingUpdateBuffers.delete(documentId);
    try {
      const account = await import('@yada/storage').then((s) => s.getAccount());
      if (account?.isAccountless || account?.emailVerified === false) return;
      if (synced.remoteDoc && !hasUnsentYjsChanges(synced.doc, synced.remoteDoc)) {
        return;
      }
      const diff = synced.remoteDoc
        ? Y.encodeStateAsUpdate(synced.doc, Y.encodeStateVector(synced.remoteDoc))
        : Y.encodeStateAsUpdate(synced.doc);
      if (diff.byteLength <= 2) {
        // No actual changes against server state (e.g. self-reverted keystrokes)
        return;
      }
      const currentCount = (updateCountSinceCompact.get(documentId) ?? 0) + updates.length;
      const currentBytes = (updateBytesSinceCompact.get(documentId) ?? 0) + diff.byteLength;
      if (currentCount >= MAX_UPDATES_BEFORE_COMPACT || currentBytes >= MAX_BYTES_BEFORE_COMPACT) {
        await writeSnapshot(synced);
        updateCountSinceCompact.set(documentId, 0);
        updateBytesSinceCompact.set(documentId, 0);
      } else {
        const seed = await getWorkspaceSeed(synced.workspaceId);

        const { deviceId: writer } = await getOrCreateDeviceKey();
        const counter = await getNextWriterCounter(documentId, synced.covers?.[writer] ?? 0);
        const nextCovers = { ...synced.covers, [writer]: Math.max(synced.covers?.[writer] ?? 0, counter) };
        const blob = await createEncryptedUpdateEnvelope(
          synced.workspaceId, documentId, diff, seed
        );
        try {
          const updateRes: unknown = await storageClient.appendUpdate(synced.workspaceId, documentId, writer, counter, blob);
          if (!synced.remoteDoc) synced.remoteDoc = new Y.Doc();
          Y.applyUpdate(synced.remoteDoc, diff, 'sync:remote');
          if (updateRes && typeof updateRes === 'object' && 'cursor' in updateRes && typeof (updateRes as any).cursor === 'string') {
            const cursor = (updateRes as any).cursor as string;
            synced.head = cursor;
            const wsManifest = latestManifestByWorkspace.get(synced.workspaceId);
            if (wsManifest) {
              wsManifest.set(synced.documentId, { id: synced.documentId, head: cursor, tombstone: false });
            }
            const converged = !hasUnsentYjsChanges(synced.doc, synced.remoteDoc);
            const fingerprint = converged ? await stateFingerprint(synced.doc) : await stateFingerprint(synced.remoteDoc);
            await recordLocalDocSync(documentId, synced.workspaceId, cursor, undefined, undefined, fingerprint, nextCovers);
          }
        } catch (err) {
          if (!(err instanceof storageClient.StorageError) || (err.status !== 404 && err.status !== 409)) throw err;
          await writeSnapshot(synced);
        }
        synced.covers = nextCovers;
        updateCountSinceCompact.set(documentId, currentCount);
        updateBytesSinceCompact.set(documentId, currentBytes);
        scheduleIdleCompaction(synced);
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('yada:lastSyncTimestamp', Date.now().toString());
      }
    } catch (err) {
      // Keep failed writes pending so flush/reconnect can actually retry them.
      pendingUpdateBuffers.set(documentId, [...updates, ...(pendingUpdateBuffers.get(documentId) ?? [])]);
      throw err;
    } finally {
      updateSyncState({ hasPending: pendingUpdateBuffers.size > 0 });
    }
  });
}

async function syncUpdateToRemote(synced: SyncedDocument, update: Uint8Array): Promise<void> {
  const { documentId, workspaceId } = synced;

  // 1. Ephemeral relay: If signaling is connected, immediately relay update to active peers
  if (isSignalingConnected()) {
    getWorkspaceSeed(workspaceId)
      .then((seed) => createEncryptedUpdateEnvelope(workspaceId, documentId, update, seed))
      .then((blob) => {
        sendCollabUpdate(workspaceId, documentId, blob);
      })
      .catch((err) => {
        logger.debug('documentSync', `Ephemeral collab relay skipped for ${documentId}:`, err);
      });
  }

  // 2. Buffer for durable server storage with adaptive debounce
  const buffer = pendingUpdateBuffers.get(documentId) ?? [];
  buffer.push(update);
  pendingUpdateBuffers.set(documentId, buffer);
  const existing = pendingSyncTimeouts.get(documentId);
  if (existing) clearTimeout(existing);
  updateSyncState({ hasPending: true });
  const debounceMs = getAdaptiveDebounceMs();
  pendingSyncTimeouts.set(documentId, setTimeout(() => {
    pendingSyncTimeouts.delete(documentId);
    if (activeDocuments.get(documentId) !== synced) return;
    flushDocumentUpdates(synced).catch((err) =>
      logger.warn('documentSync', 'Upload failed; local changes remain pending', err)
    );
  }, debounceMs));
}

export async function flushPendingSync(): Promise<void> {
  // Wait for in-flight pulls/writes as well as debounce timers. Pulls can discover
  // unsent local data, so keep draining until the queue is actually empty.
  do {
    for (const timer of pendingSyncTimeouts.values()) clearTimeout(timer);
    pendingSyncTimeouts.clear();
    const operations = [...documentOperations.values()];
    for (const documentId of pendingUpdateBuffers.keys()) {
      const synced = activeDocuments.get(documentId);
      if (!synced) throw new Error(`Pending document ${documentId} is not open`);
      operations.push(flushDocumentUpdates(synced));
    }
    const results = await Promise.allSettled(operations);
    const failed = results.find((r) => r.status === 'rejected');
    if (failed?.status === 'rejected') throw failed.reason;
  } while (pendingUpdateBuffers.size > 0 || documentOperations.size > 0);
}

function classifySyncError(err: unknown): 'offline' | 'auth' | 'seed-unavailable' | 'unknown' {
  const status = (err as any)?.status;
  if (typeof status === 'number') {
    if (status === 401 || status === 403) return 'auth';
    if (status === 429 || status === 502 || status === 503 || status === 504 || status === 0) return 'offline';
  }

  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('circuitbreaker') || msg.includes('429') || msg.includes('rate limit') || msg.includes('too many requests')) return 'offline';
    if (
      msg.includes('failed to fetch') ||
      msg.includes('networkerror') ||
      msg.includes('network') ||
      msg.includes('timeout') ||
      msg.includes('econnrefused')
    ) {
      return 'offline';
    }
    if (
      msg.includes('unauthenticated') ||
      msg.includes('unauthorized') ||
      msg.includes('jwt') ||
      msg.includes('forbidden') ||
      msg.includes('token expired') ||
      msg.includes('session expired')
    ) {
      return 'auth';
    }
    if (
      err.name === 'WorkspaceSeedNotFoundError' ||
      err.name === 'OperationError' ||
      msg.includes('seed not found') ||
      msg.includes('workspace seed') ||
      msg.includes('wrapped seed') ||
      msg.includes('decrypt') ||
      msg.includes('unwrap') ||
      msg.includes('operationerror')
    ) {
      return 'seed-unavailable';
    }
  }
  return 'unknown';
}

export function closeDocument(documentId: string): void {
  const idleTimer = pendingIdleCompacts.get(documentId);
  if (idleTimer !== undefined) {
    clearTimeout(idleTimer);
    pendingIdleCompacts.delete(documentId);
  }
  const timer = pendingSyncTimeouts.get(documentId);
  if (timer !== undefined) clearTimeout(timer);
  pendingSyncTimeouts.delete(documentId);
  pendingOpenSyncs.delete(documentId);
  if (pendingOpenSyncs.size === 0 && openSyncDebounceTimer !== null) {
    clearTimeout(openSyncDebounceTimer);
    openSyncDebounceTimer = null;
  }
  const synced = activeDocuments.get(documentId);
  if (!synced) return;

  synced.persistence.destroy();
  synced.doc.destroy();
  synced.remoteDoc?.destroy();

  activeDocuments.delete(documentId);
}

export function getDocument(documentId: string): SyncedDocument | undefined {
  return activeDocuments.get(documentId);
}

export async function compactDocument(synced: SyncedDocument): Promise<void> {
  const existingTimer = pendingIdleCompacts.get(synced.documentId);
  if (existingTimer) {
    clearTimeout(existingTimer);
    pendingIdleCompacts.delete(synced.documentId);
  }
  await runDocumentOperation(synced, async () => {
    await writeSnapshot(synced);
    updateCountSinceCompact.set(synced.documentId, 0);
    updateBytesSinceCompact.set(synced.documentId, 0);
  });
}

export function closeAllDocuments(): void {
  for (const documentId of [...activeDocuments.keys()]) closeDocument(documentId);
}

export async function pushAllLocalDocumentsToServer(
  workspaceId: string,
  options: { knownWorkspaceIds?: string[]; createDefaultAppDocuments?: boolean } = {},
): Promise<number> {
  const account = await import('@yada/storage').then((s) => s.getAccount());
  if (account?.isAccountless || account?.emailVerified === false) return 0;

  const { listAllOPFSDocumentIds } = await import('./OPFSPersistence.js');
  const { extractAppIdFromDocumentId, cojoinYDocs, cojoinLocalStorageIntoYDoc, sanitizeStorageMap } = await import('./reconciliation.js');
  const allDocIds = await listAllOPFSDocumentIds();
  const localSyncRecords = await getAllLocalDocSync();
  const knownWorkspaceIds = [...new Set([workspaceId, ...(options.knownWorkspaceIds ?? [])])];
  console.log(`[pushAllLocalDocumentsToServer] Found ${allDocIds.length} local OPFS documents`);

  const processedActiveDocs = new Set<string>();
  let pushedCount = 0;
  const failures: Array<{ documentId: string; error: unknown }> = [];

  for (const docId of allDocIds) {
    try {
      const recordedWorkspaceId = localSyncRecords.get(docId)?.workspaceId;
      const filenameWorkspaceId = knownWorkspaceIds.find((id) => docId.endsWith(`-${id}`));
      const sourceWorkspaceId = recordedWorkspaceId || filenameWorkspaceId;
      if (sourceWorkspaceId && sourceWorkspaceId !== workspaceId) continue;

      if (docId.startsWith('@yada/app-community-votes')) {
        console.warn(`[pushAllLocalDocumentsToServer] Preserving unsupported local document ${docId}`);
        failures.push({
          documentId: docId,
          error: new Error('This public app document is excluded from private workspace restore.'),
        });
        continue;
      }

      const appId = extractAppIdFromDocumentId(docId);
      const activeDocId = `${appId}-${workspaceId}`;

      if (docId !== activeDocId) {
        const sourceDoc = new Y.Doc();
        const persistence = new OPFSPersistence(docId, sourceDoc);
        await new Promise<void>((resolve) => {
          persistence.on('synced', () => resolve());
        });

        const targetSynced = await openDocument(workspaceId, activeDocId, appId);
        cojoinYDocs(appId, targetSynced.doc, sourceDoc);
        cojoinLocalStorageIntoYDoc(appId, targetSynced.doc);
        sanitizeStorageMap(appId, targetSynced.doc.getMap('storage'));

        persistence.destroy();
        sourceDoc.destroy();

        processedActiveDocs.add(activeDocId);
        await syncToRemote(targetSynced);
        await verifyLocalDocumentOnServer(targetSynced);
        pushedCount++;
      } else {
        const sourceSynced = await openDocument(workspaceId, docId, appId);
        cojoinLocalStorageIntoYDoc(appId, sourceSynced.doc);
        sanitizeStorageMap(appId, sourceSynced.doc.getMap('storage'));

        processedActiveDocs.add(activeDocId);
        const snapshot = Y.encodeStateAsUpdate(sourceSynced.doc);
        if (snapshot.length > 1) {
          await syncToRemote(sourceSynced);
          await verifyLocalDocumentOnServer(sourceSynced);
          pushedCount++;
        }
      }
    } catch (err) {
      console.warn(`[pushAllLocalDocumentsToServer] Failed to process document ${docId}:`, err);
      failures.push({ documentId: docId, error: err });
    }
  }

  const firstPartyApps = [
    '@yada/app-habits',
    '@yada/app-docs',
    '@yada/app-todo',
    '@yada/app-calendar',
    '@yada/app-shopping-list',
    '@yada/app-finance',
    '@yada/app-training',
    '@yada/app-contacts',
    '@yada/app-meal-planner',
    '@yada/app-chat',
    '@yada/app-emoji',
    '@yada/app-groups',
    '@yada/app-content-distribution',
  ];

  for (const appId of options.createDefaultAppDocuments === false ? [] : firstPartyApps) {
    const activeDocId = `${appId}-${workspaceId}`;
    if (processedActiveDocs.has(activeDocId)) continue;

    try {
      const targetSynced = await openDocument(workspaceId, activeDocId, appId);
      const hadLocal = cojoinLocalStorageIntoYDoc(appId, targetSynced.doc);
      sanitizeStorageMap(appId, targetSynced.doc.getMap('storage'));

      const snapshot = Y.encodeStateAsUpdate(targetSynced.doc);
      if (hadLocal || snapshot.length > 1) {
        await syncToRemote(targetSynced);
        await verifyLocalDocumentOnServer(targetSynced);
        pushedCount++;
      }
    } catch (err) {
      console.warn(`[pushAllLocalDocumentsToServer] Failed to cojoin localStorage for ${appId}:`, err);
      failures.push({ documentId: `${appId}-${workspaceId}`, error: err });
    }
  }

  if (failures.length > 0) {
    const failedIds = failures.map((failure) => failure.documentId).join(', ');
    throw new Error(`Local restore incomplete: ${failures.length} document(s) failed: ${failedIds}`);
  }

  console.log(`[pushAllLocalDocumentsToServer] Successfully pushed ${pushedCount} local documents to remote`);
  return pushedCount;
}

/**
 * Removes every local store of documents, keys and app data on this device. Runs
 * every step even when one fails, then throws `LocalPurgeError` naming what was
 * not removed. Device identity (`yada_device_keys`) is deliberately kept.
 */
export async function clearAllDocumentDatabases(): Promise<void> {
  closeAllDocuments();
  clearSeedCache();
  clearAssetBlobCache();
  const steps: Array<[string, () => Promise<void>]> = [
    ['key vault', clearLocalWrappedSeedVault],
    ['documents', clearAllOPFSDocuments],
    ['sync state', clearAllLocalDocSync],
    ['document metadata', () => deleteIndexedDatabase(DOCUMENT_METADATA_DB)],
    ['asset cache', () => deleteIndexedDatabase(ASSET_CACHE_DB)],
    ['device sync journal', () => deleteIndexedDatabase(WORKSPACE_HYDRATION_JOURNAL_DB)],
    ['app data', clearAppDatabases],
  ];
  const failures: string[] = [];
  for (const [label, step] of steps) {
    try {
      await step();
    } catch (err) {
      failures.push(`${label}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  if (failures.length) throw new LocalPurgeError(failures);
}

/** Number of documents with edits not yet uploaded in this session. */
export function getPendingDocumentCount(): number {
  return pendingUpdateBuffers.size;
}
